import { validTimestamp } from "../format.js";
import type { DiaryEntry } from "./entries.js";

export interface PublishedDiaryLesson {
  appointmentId: string;
  startsAt: string;
}

export interface DiaryPublication extends PublishedDiaryLesson {
  returnedAt: string;
}

export function publishedLessonDiaryState(lesson: PublishedDiaryLesson) {
  return { publishedLesson: { appointmentId: lesson.appointmentId, startsAt: lesson.startsAt, returnedAt: new Date().toISOString() } };
}

export function diaryPublication(state: unknown): DiaryPublication | null {
  if (!state || typeof state !== "object" || !("publishedLesson" in state)) return null;
  const value = state.publishedLesson;
  if (!value || typeof value !== "object") return null;
  if (!("appointmentId" in value) || typeof value.appointmentId !== "string") return null;
  if (!("startsAt" in value) || typeof value.startsAt !== "string" || !validTimestamp(value.startsAt)) return null;
  if (!("returnedAt" in value) || typeof value.returnedAt !== "string" || !validTimestamp(value.returnedAt)) return null;
  return { appointmentId: value.appointmentId, startsAt: value.startsAt, returnedAt: value.returnedAt };
}

const SKIP = new Set(["canceled", "cancelled", "rejected", "waiting", "no-show", "no_show", "absent"]);

/** Pick from the server-authorised diary, including a next lesson already under way. */
export function nextLessonAfterPublication(
  entries: readonly DiaryEntry[],
  publication: DiaryPublication,
  ownTutorId: string | null,
): (DiaryEntry & { anonymous: false }) | null {
  const completed = entries.find((entry) => entry.appointmentId === publication.appointmentId);
  const tutorId = completed?.tutorId ?? ownTutorId;
  const after = Date.parse(publication.startsAt);
  const now = Date.parse(publication.returnedAt);
  return entries
    .filter((entry): entry is DiaryEntry & { anonymous: false } =>
      !entry.anonymous &&
      (tutorId === null || entry.tutorId === tutorId) &&
      entry.appointmentId !== publication.appointmentId &&
      Date.parse(entry.startsAt) > after &&
      Date.parse(entry.endsAt) > now &&
      !SKIP.has(entry.status.toLowerCase()) &&
      !SKIP.has(entry.bookingStatus.toLowerCase()) &&
      !SKIP.has(entry.attendanceStatus?.toLowerCase() ?? ""),
    )
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))[0] ?? null;
}
